/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2019-09-23 19:58:17
 *
 *******************************************************/
//@flow

import _ from 'lodash';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Card, ButtonGroup, Button, SkeletonDisplayText, Link } from '@shopify/polaris';
import { EditMajor, DeleteMajor } from '@shopify/polaris-icons';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';

import ConfirmRemoveModal from 'src/components/views/merge-values/confirm-remove-modal';
import NoMergeValues from 'src/components/views/merge-values/no-merge-values';
import { routes } from 'src/routes';
import history from 'src/routes/history';

import * as MergeValuesActions from 'actions/merge-values-actions';

type Props = {
  items: any[]
};

type ComposedProps = Props & WithI18nProps;

type State = {
  activeModalRemove: boolean,
  selectedType: string
};
class OptionsTable extends Component<ComposedProps, State> {
  state = {
    activeModalRemove: false,
    selectedType: ''
  };

  render() {
    const { types, loading, deleting, i18n } = this.props;
    const { activeModalRemove } = this.state;

    return (
      <Card>
        <Card.Section>
          <div className="Polaris-DataTable BC-DataTable__MergeValuesOptions">
            <div className="Polaris-DataTable__ScrollContainer" style={{ marginLeft: 0 }}>
              <table className="Polaris-DataTable__Table">
                <thead>
                  <tr>
                    <th className="Polaris-DataTable__Cell Polaris-DataTable__Cell--header">
                      {i18n.translate('AdminHTML.MergeValues.OptionsTable.tableHeading1')}
                    </th>
                    <th className="Polaris-DataTable__Cell Polaris-DataTable__Cell--header">
                      {i18n.translate('AdminHTML.MergeValues.OptionsTable.tableHeading2')}
                    </th>
                    <th className="Polaris-DataTable__Cell Polaris-DataTable__Cell--header Polaris-DataTable__Cell--numeric">
                      {i18n.translate('AdminHTML.MergeValues.OptionsTable.tableHeading3')}
                    </th>
                  </tr>
                </thead>
                {loading ? (
                  <tbody>
                    {[...Array(3).keys()].map((item, key) => (
                      <tr key={key}>
                        {[...Array(3).keys()].map((item, key) => (
                          <td className="Polaris-DataTable__Cell" key={key}>
                            <SkeletonDisplayText size="small" />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                ) : !_.isEmpty(types) ? (
                  <tbody>
                    {types.map((item, key) => {
                      const { status, type } = item;
                      return (
                        <tr className="Polaris-DataTable__TableRow" key={key}>
                          {/* Status */}
                          <td className="Polaris-DataTable__Cell">
                            <label className="BC-Checkbox-Status">
                              <input
                                type="checkbox"
                                checked={status === 'active'}
                                onChange={e => this.updateStatus(e, type)}
                              />
                              <span />
                            </label>
                          </td>

                          {/* Name */}
                          <td className="Polaris-DataTable__Cell">
                            <Link
                              removeUnderline
                              onClick={() => {
                                this.handleEditMergeValues(type);
                              }}>
                              {type}
                            </Link>
                          </td>

                          {/* Action */}
                          <td className="Polaris-DataTable__Cell Polaris-DataTable__Cell-Action">
                            {this.renderAction(type)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                ) : (
                  <tbody />
                )}
              </table>
              {_.isEmpty(types) && !loading ? <NoMergeValues /> : ''}
            </div>
          </div>
        </Card.Section>
        <ConfirmRemoveModal
          active={activeModalRemove}
          handleChange={this.handleActiveOptionModalRemove}
          handleRemove={this.deleteMergeValuesType}
          removing={deleting}
          title={i18n.translate('AdminHTML.MergeValues.OptionsTable.confirmRemoveModal_Title')}
          content={i18n.translate('AdminHTML.MergeValues.OptionsTable.confirmRemoveModal_Content')}
        />
      </Card>
    );
  }

  // Render action button for per filter
  renderAction = (type: string) => {
    return (
      <ButtonGroup segmented>
        <Button size="slim" icon={EditMajor} onClick={() => this.handleEditMergeValues(type)} />
        <Button
          size="slim"
          icon={DeleteMajor}
          onClick={() => {
            this.updateSelected(type);
            this.handleActiveOptionModalRemove();
          }}
        />
      </ButtonGroup>
    );
  };

  // Handle view/edit button
  handleEditMergeValues = (type: string) => {
    history.push(routes.mergeValues.edit(type));
  };

  // Update selected type
  updateSelected = (type: string) => {
    this.setState(({ selectedType }) => ({
      selectedType: type
    }));
  };

  // Handle open/close modal delete
  handleActiveOptionModalRemove = () => {
    this.setState(({ activeModalRemove }) => ({
      activeModalRemove: !activeModalRemove
    }));
  };

  // Handle click status
  updateStatus = (e: Object, type: string) => {
    const { updateStatus } = this.props;
    updateStatus(type, e.target.checked ? 'active' : 'disabled');
  };

  // Handle click delete button
  deleteMergeValuesType = async () => {
    const { selectedType } = this.state;
    const { deleteMergeValuesType } = this.props;

    await deleteMergeValuesType(selectedType);
    this.handleActiveOptionModalRemove();
  };
}

const mapStateToProps = (state, props) => {
  const { mergeValues } = state;
  return {
    types: _.get(mergeValues, 'types', []),
    loading: _.get(mergeValues, 'getNamesLoading', false),
    updateStatusLoading: _.get(mergeValues, 'updateStatusLoading', false),
    deleting: _.get(mergeValues, 'deleting', false)
  };
};

const mapDispatchToProps = (dispatch, ownProps) => ({
  deleteMergeValuesType: type =>
    dispatch(MergeValuesActions.deleteMergeValuesType(type, ownProps.i18n)),
  updateStatus: (type, status) =>
    dispatch(MergeValuesActions.updateMergeValuesTypeStatus(type, status, ownProps.i18n))
});

export default withI18n()(connect(mapStateToProps, mapDispatchToProps)(OptionsTable));
